import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { Price } from '@provider-portal/common/api/types';

import { selectMenuDishesEntities, selectMenuOptionsEntities } from 'rdx/menu';
import { UIMenuItemAvailabilityStatus } from 'rdx/menu/types';
import { toUIAvailabilityStatus } from 'rdx/menu/utils';
import { getLocalisedStringValue } from 'utils/locales';

// original example https://dev.to/mrmuhammadali/managing-list-of-form-fields-with-formik-through-example-7o5

export interface FormItem {
  name: string | null;
  availability: UIMenuItemAvailabilityStatus;
  price: string;
  description: string | null;
  image: string | null;
}

interface InitialValues {
  items: {
    [key: number]: FormItem;
  };
}

export const getPriceAsString = (price?: Price | null, showCurrency?: boolean): string => {
  if (!price) {
    return '';
  }
  return showCurrency ? price.price_str || '' : price.value.toFixed(2) || '';
};

export const useInitialValues = (): InitialValues => {
  const dishes = useSelector(selectMenuDishesEntities);
  const options = useSelector(selectMenuOptionsEntities);

  const itemsFields = useMemo(() => {
    const items = [
      ...(dishes ? Object.values(dishes) : []),
      ...(options ? Object.values(options) : []),
    ];
    return items
      .filter((item) => item)
      .reduce((res, item) => {
        if (!item) {
          return res;
        }

        res[item.id] = {
          name: getLocalisedStringValue(item.name),
          availability: toUIAvailabilityStatus(item.availability),
          price: getPriceAsString(item.price),
          description: getLocalisedStringValue(item.description),
          image: item?.images?.menu_item_list_v1?.aspect_ratio_map?.original?.['1x'],
        };

        return res;
      }, {} as { [key: number]: FormItem });
  }, [dishes, options]);

  return {
    items: itemsFields,
  };
};
